import { useEffect, useRef, useState } from "react";
import { Pause, Play, Volume2, VolumeX } from "lucide-react";

export const MEDIA = {
  rob: { src: "/media/rob-757.mp4", poster: "/media/rob-757.jpg", label: "Rob Resh team film: Virginia Beach to Greater Richmond" },
  cover: { src: "/media/cover-undeniable.mp4", poster: "/media/cover-undeniable.jpg", label: "757 Realty and 804 Homes brand film: Real Estate Simplified" },
};

const CtrlBtn = ({ onClick, label, children, testId }) => (
  <button type="button" onClick={onClick} aria-label={label} data-testid={testId}
    className="inline-flex h-9 items-center gap-2 rounded-full border border-white/30 px-4 text-[13px] text-white transition-colors hover:bg-white hover:text-ink">
    {children}
  </button>
);

/** Plays (muted) only while on screen; pauses when scrolled away or when the visitor pauses it. */
function useInViewPlayback(ref, userPaused) {
  useEffect(() => {
    const v = ref.current;
    if (!v || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !userPaused.current) v.play().catch(() => {});
      else if (!e.isIntersecting) v.pause();
    }, { threshold: 0.2 });
    io.observe(v);
    return () => io.disconnect();
  }, [ref, userPaused]);
}

/** Full-width film strip. Nothing is layered on the frame — caption and controls sit BELOW it so the film's own words stay visible. */
export const VideoBand = ({ media, caption, testId, frameClass = "", className = "" }) => {
  const ref = useRef(null);
  const userPaused = useRef(false);
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);
  useInViewPlayback(ref, userPaused);

  const toggleSound = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) v.play().catch(() => {});
    userPaused.current = false;
    setMuted(v.muted);
    setPaused(false);
  };
  const togglePlay = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {}); else v.pause();
    userPaused.current = v.paused;
    setPaused(v.paused);
  };

  return (
    <section className={`bg-ink ${className}`} data-testid={testId}>
      <video ref={ref} src={media.src} poster={media.poster} muted loop playsInline preload="metadata"
        onPause={() => setPaused(true)} onPlay={() => setPaused(false)}
        className={`block w-full bg-black object-contain ${frameClass}`} data-testid={`${testId}-video`} aria-label={media.label} />
      <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-3 px-5 py-3 sm:px-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/75" data-testid={`${testId}-caption`}>{caption}</p>
        <div className="flex gap-2">
          <CtrlBtn onClick={toggleSound} label={muted ? "Turn sound on" : "Mute"} testId={`${testId}-sound`}>
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />} {muted ? "Sound on" : "Mute"}
          </CtrlBtn>
          <CtrlBtn onClick={togglePlay} label={paused ? "Play" : "Pause"} testId={`${testId}-play`}>
            {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </CtrlBtn>
        </div>
      </div>
    </section>
  );
};
